import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { formatDateTime } from "../lib/format";
import { Send, Loader2, Trash2, Pencil, Check, CheckCheck } from "lucide-react";
import { Button } from "./ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "./ui/alert-dialog";

const ReadTicks = ({ m, cli }) => {
  const status = m.read ? "letto" : m.delivered_at ? "consegnato" : "inviato";
  const base = cli ? "text-[#fed7aa]" : "text-[#d1d5db]";
  const title = m.read ? `Letto${m.read_at ? ` · ${formatDateTime(m.read_at)}` : ""}` : status === "consegnato" ? "Consegnato" : "Inviato";
  const Icon = status === "inviato" ? Check : CheckCheck;
  return (
    <span title={title} aria-label={title} data-testid={`msg-read-status-${m.id}`} data-status={status} className="inline-flex items-center ml-1.5 align-middle">
      <Icon className={`h-3.5 w-3.5 ${m.read ? "text-[#38bdf8]" : base}`} strokeWidth={2.5} />
    </span>
  );
};

// Thread messaggi riutilizzabile per studio e cliente.
// selfRole: "studio" | "cliente". getUrl/postUrl relativi (senza /api).
export default function MessageThread({ getUrl, postUrl, selfRole, blockedReason = "", blockedAction = null, client = api, onLoaded = null, showReadStatus = false, canDelete = false }) {
  const [toDelete, setToDelete] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editText, setEditText] = useState("");
  const saveEdit = async () => {
    if (!editText.trim()) return;
    try {
      const { data } = await client.patch(`${postUrl}/${editing}`, { body: editText.trim() });
      setMessages((ms) => ms.map((x) => (x.id === data.id ? data : x)));
      setEditing(null);
    } catch (e) {
      const { toast } = await import("sonner");
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await client.get(getUrl);
      setMessages((prev) => (JSON.stringify(prev) === JSON.stringify(data) ? prev : data));
      onLoaded?.();
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getUrl]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || blockedReason) return;
    setSending(true);
    try {
      const { data } = await client.post(postUrl, { body: text.trim() });
      setMessages((m) => [...m, data]);
      setText("");
    } catch (e) {
      const { toast } = await import("sonner");
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const remove = async () => {
    const m = toDelete;
    setToDelete(null);
    try {
      await client.delete(`${postUrl}/${m.id}`);
      setMessages((ms) => ms.filter((x) => x.id !== m.id));
    } catch (e) {
      const { toast } = await import("sonner");
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="flex flex-col h-[60vh]" data-testid="message-thread">
      <div className="flex-1 overflow-y-auto space-y-3 pr-1">
        {loading ? (
          <div className="h-full flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-amber-500" /></div>
        ) : messages.length === 0 ? (
          <div className="h-full flex items-center justify-center text-sm text-slate-500">Nessun messaggio. Inizia la conversazione.</div>
        ) : (
          messages.map((m) => {
            const mine = m.sender_role === selfRole;
            const cli = selfRole === "cliente";
            const bubble = cli
              ? (mine ? "bg-[#9a3412] text-[#ffffff] rounded-br-sm" : "bg-[#f1f5f9] text-[#0f172a] rounded-bl-sm border border-[#cbd5e1]")
              : (mine ? "text-[#ffffff] rounded-br-sm" : "bg-[#F3F4F6] text-[#111827] rounded-bl-sm border border-[#e5e7eb]");
            const meta = cli ? (mine ? "text-[#fff7ed]" : "text-[#92400e]") : (mine ? "text-[#ffffff]" : "text-[#1f2937]");
            const time = cli ? (mine ? "text-[#ffedd5]" : "text-[#475569]") : (mine ? "text-[#e5e7eb]" : "text-[#4b5563]");
            const bubbleStyle = !cli && mine ? { backgroundColor: "color-mix(in srgb, var(--accent-600) 75%, #000)" } : undefined;
            return (
              <div key={m.id} className={`group flex items-center gap-1.5 ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                {canDelete && editing !== m.id && (
                  <span className={`${mine ? "" : "order-last"} flex items-center opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity`}>
                    {mine && (
                      <button type="button" onClick={() => { setEditing(m.id); setEditText(m.body); }} title="Modifica messaggio" data-testid={`msg-edit-${m.id}`}
                        className="p-1.5 rounded-md text-[#6b7280] hover:text-[#1d4ed8] hover:bg-[#dbeafe] transition-colors">
                        <Pencil className="h-4 w-4" />
                      </button>
                    )}
                    <button type="button" onClick={() => setToDelete(m)} title="Elimina messaggio" data-testid={`msg-delete-${m.id}`}
                      className="p-1.5 rounded-md text-[#6b7280] hover:text-[#dc2626] hover:bg-[#fee2e2] transition-colors">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </span>
                )}
                <div className={`max-w-[78%] rounded-2xl px-4 py-2.5 ${bubble}`} style={bubbleStyle} data-testid={`msg-bubble-${m.id}`}>
                  <div className={`text-[10px] font-medium mb-0.5 ${meta}`}>
                    {m.sender_name} · {m.sender_role === "studio" ? "Studio" : "Cliente"}
                  </div>
                  {editing === m.id ? (
                    <div className="min-w-[260px]">
                      <textarea value={editText} onChange={(e) => setEditText(e.target.value)} rows={3} autoFocus data-testid={`msg-edit-input-${m.id}`}
                        className="w-full rounded-md border border-[#d1d5db] bg-[#ffffff] px-2 py-1.5 text-sm text-[#111827] focus:outline-none" />
                      <div className="mt-1.5 flex justify-end gap-1.5">
                        <button type="button" onClick={() => setEditing(null)} data-testid={`msg-edit-cancel-${m.id}`}
                          className="rounded-md bg-[#ffffff] px-2.5 py-1 text-xs font-medium text-[#111827] hover:bg-[#F3F4F6] transition-colors">Annulla</button>
                        <button type="button" onClick={saveEdit} disabled={!editText.trim()} data-testid={`msg-edit-save-${m.id}`}
                          className="rounded-md bg-[#111827] px-2.5 py-1 text-xs font-semibold text-[#ffffff] hover:bg-[#1f2937] transition-colors disabled:opacity-50">Salva</button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm whitespace-pre-wrap break-words">{m.body}</div>
                  )}
                  <div className={`text-[10px] mt-1 font-mono ${time}`}>
                    {formatDateTime(m.created_at)}
                    {m.edited_at && <span className="ml-1.5 italic font-sans" data-testid={`msg-edited-${m.id}`}>· modificato</span>}
                    {mine && <ReadTicks m={m} cli={cli} />}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={endRef} />
      </div>

      {blockedReason && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800" role="alert" data-testid="message-blocked-warning">
          <span>{blockedReason}</span>
          {blockedAction}
        </div>
      )}
      <form onSubmit={send} className="mt-3 flex items-center gap-2 border-t border-slate-800 pt-3">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Scrivi un messaggio…"
          data-testid="message-input"
          className="flex-1 h-11 rounded-lg bg-slate-900/60 border border-slate-700 px-4 text-sm text-slate-100 focus:outline-none focus:border-amber-500/60"
        />
        <Button type="submit" disabled={sending || !!blockedReason} data-testid="message-send"
          className="h-11 bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold px-4">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </form>
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="msg-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Sei sicuro di voler eliminare questo messaggio?</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="msg-delete-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="msg-delete-confirm" className="bg-[#dc2626] hover:bg-[#b91c1c] text-[#ffffff]">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
